import React, { useEffect, useState, Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import parse from 'html-react-parser';


// components

import SearchForm from './search-form';
import SearchResults from './search-results';
import { SearchAgain } from '../common';

// utilities

import { configs } from '../../../constants/configs/configs';

// hooks

import useAgentListing from './useAgentListing';
import type { AgentResultDetails } from './useAgentListing';
import useDisclosure from '../../../hooks/useDisclosure';
import GAEPMainContent from '../gaep/GAEPMainContent';

// styles

const StyledPagination = styled.div`
  .pagination-agents {
      justify-content: flex-end;
      column-gap: 10px;
      display: -ms-flexbox;
      display: flex;
      padding-left: 0;
      list-style: none;
      border-radius: 0.25rem;
  }
`;

// props

interface AgentListingContainerProps {
}

interface SelectedPageNum {
    selected: number;
}

interface SearchCriteria {
  isZipResults: boolean;
  zipcode?: string;
  distance?: string;
  languagesSpoken?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
}

export interface LocationStateProps {
    searchCriteria: SearchCriteria;
    agentListData: AgentResultDetails[];
    isError: boolean;

}

function useQuery() {
    const { search } = useLocation();

    return React.useMemo(() => new URLSearchParams(search), [search]);
}


// constants

const PAGINATION_COUNT_THRESHOLD = 10;
const SHOW_MODAL_MODE: string = '1';
const SAVED_AGENT_DATA: string = 'agentListData';

function AgentListingContainer(props: AgentListingContainerProps) {

    const [ isShowModalFromWP, setShowModalFromWP ] = useState(false);
    const { data, isError, searchAgentListingByLocation, searchAgentListingByName } = useAgentListing();
    const [ isSearchError, setSearchError ] = useState(false);
    const [ savedResults, setSavedResults ] = useState<any | null>(null);
    const [ pageNumber, setPageNumber ] = useState<number | null>(null);
    const [ searchCriteria, setSearchCriteria ] = useState<SearchCriteria | null>(null);
    const { isOpen: showSearchForm, onOpen: openSearchForm, onClose: closeSearchForm } = useDisclosure();
    const query = useQuery();
    const location = useLocation();
    const intlApi = useIntl();

    const {
        routes: {
            ANON_GA_GAEPS
        }
    } = configs;

    useEffect(() => {
        document.title = 'Agent Listing';
        const modalMode = query.get('m');
        if (modalMode === SHOW_MODAL_MODE) {
            setShowModalFromWP(true);
            openSearchForm();
        } else if (location.state?.agentListData) {
            setSavedResults({ agentList: location.state.agentListData, searchCriteria: location.state.searchCriteria });
        } else if (location.state?.isError) {
            setSearchError(true);
        } else {
            const savedData = window.localStorage.getItem(SAVED_AGENT_DATA);
            if (savedData) {
                const parsedSaveData = JSON.parse(savedData);
                setSavedResults({ agentList: parsedSaveData.data, searchCriteria: parsedSaveData.searchCriteria });
            }
        }
    }, []);

    useEffect(() => {
        if (data) {
            setSavedResults({ agentList: data.payload.content, searchCriteria: searchCriteria || {} });
            window.localStorage.setItem(SAVED_AGENT_DATA, JSON.stringify({ searchCriteria: searchCriteria || {}, data: data.payload.content }));
        }
    }, [data, searchCriteria]);


    const MainText = () => {
        return (
            <>
                    <p><FormattedMessage id='ga.agents.main.text.1' /></p>
                    <p><FormattedMessage id='ga.agents.main.text.2' /></p>
                    <p>
                        {parse(intlApi.formatMessage({ id: 'ga.agents.main.text.3' }, { webBrokerUrl: ANON_GA_GAEPS}))}
                    </p>
            </>
        )
    };

    const SearchAgents = () => {
        return (
            <div className="grid-row">
                <div className="desktop:grid-col-8 tablet:grid-col-8 mobile:grid-col-12 select-list_results_col">
                    <div className="grid-row select-list_results_col_row">
                        <div className="desktop:grid-col-auto tablet:grid-col-auto mobile:grid-col-12 heading">
                            <h2>
                                <FormattedMessage id='ga.certified_agents' values={{ count: data && data.length }} />
                            </h2>
                        </div>
                        <div className="desktop:grid-col tablet:grid-col mobile:grid-col-12 heading_link">
                            <div className="heading_link_zipcode">
                                <span className="h2">
                                    { savedResults?.searchCriteria?.zipcode &&
                                        <FormattedMessage id='ga.agents.found_zip' values={{ zipcode: savedResults?.searchCriteria?.zipcode }}/>
                                    }
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="desktop:grid-col-4 tablet:grid-col-4 mobile:grid-col-12 text-right">
                    <SearchAgain onClickHandler={openSearchForm} />
                </div>
            </div>
        )
    };

    const onSearchFormHandler = (isZipcodeSearch: boolean, inputFormData: any) => {
        setShowModalFromWP(false);
        closeSearchForm();
        setSearchCriteria({ isZipResults: isZipcodeSearch, ...inputFormData });
        setPageNumber(0);
        isZipcodeSearch ? searchAgentListingByLocation(pageNumber, {...inputFormData}) : searchAgentListingByName(pageNumber, {...inputFormData});
    };

    const onHideSearchFormHandler = () => {
        if (isShowModalFromWP) {
            window.history.back();
        } else {
            closeSearchForm();
        }
    };

    const onPageNumberChange = (pageObj: SelectedPageNum) => {
        setPageNumber(pageObj.selected);
        searchCriteria?.isZipResults ? searchAgentListingByLocation(pageObj.selected, {...searchCriteria}) : searchAgentListingByName(pageObj.selected, {...searchCriteria});
    };

    const hasNoResults = savedResults?.agentList && savedResults?.agentList.length < 1;
    const hasError = isError || isSearchError;

    return (
        <Fragment>
            <GAEPMainContent titleId='ga.certified_agents_heading' mainContentId='ga.certified_agents.content' mainContentSecondLineId='ga.certified_agents.content.second_line' mainContentThirdLineId='ga.certified_agents.content.third_line' />
            <main className="usa-layout-docs usa-section gi-main-section" id="gaep-agent-listing">

                <div className="grid-container gi-grid-container">
                    <MainText />
                    <SearchAgents />
                    {
                        hasError &&
                        <>
                            <p/>
                            <div><FormattedMessage id='ga.search.error' /></div>
                        </>
                    }
                    {
                        !hasError && hasNoResults &&
                        <p>
                            <div><FormattedMessage id='ga.search.no_results' /></div>
                        </p>
                    }
                    {
                        !hasError && !hasNoResults &&
                        <>
                            <SearchResults data={savedResults?.agentList} isShowZipResults={savedResults?.searchCriteria?.isZipResults} />
                            { data?.payload.content.length > PAGINATION_COUNT_THRESHOLD &&
                                <StyledPagination>
                                    <ReactPaginate
                                        previousLabel={<FormattedMessage id="delegation_pagination_prev"/>}
                                        nextLabel={<FormattedMessage id="delegation_pagination_next"/>}
                                        pageCount={Math.ceil(data.payload.pageable.totalElements / PAGINATION_COUNT_THRESHOLD)}
                                        marginPagesDisplayed={2}
                                        pageRangeDisplayed={5}
                                        onPageChange={onPageNumberChange}
                                        pageClassName="page-item"
                                        pageLinkClassName="page-link"
                                        previousClassName="page-item"
                                        previousLinkClassName="page-link"
                                        nextClassName="page-item"
                                        nextLinkClassName="page-link"
                                        breakLabel="..."
                                        breakClassName="page-item"
                                        breakLinkClassName="page-link"
                                        containerClassName="pagination-agents"
                                        activeClassName="active"
                                        forcePage={pageNumber}
                                        hrefBuilder={() => {
                                            return '#';
                                        }}
                                    />
                                </StyledPagination>
                            }
                        </>
                    }
                </div>
                {
                    showSearchForm && <SearchForm show={showSearchForm} onSearch={onSearchFormHandler} onHide={onHideSearchFormHandler} />
                }
            </main>
        </Fragment>
    )
};

export default AgentListingContainer;